const SITE_ORIGIN = 'https://www.revont.ai';
const SOCIAL_IMAGE = `${SITE_ORIGIN}/link-preview.png`;

type RouteMetadata = {
  title: string;
  description: string;
  indexable: boolean;
};

const ROUTE_METADATA: Record<string, RouteMetadata> = {
  '/': {
    title: 'Revont | The AI-Native Sales Intelligence Platform',
    description:
      'Revont is the AI-native sales intelligence platform for B2B teams, connecting live meeting coaching, deal context, workspace memory, and follow-up actions.',
    indexable: true,
  },
  '/product': {
    title: 'Sales Intelligence Software for B2B Teams | Revont',
    description:
      'See how Revont connects live meeting coaching, customer and deal context, notes, action items, searchable workspace memory, and sales workflows.',
    indexable: true,
  },
  '/mission': {
    title: 'Our Mission | Revont',
    description:
      'Learn why Revont brings conversation intelligence, deal context, and sales workflows together to help teams make every customer interaction count.',
    indexable: true,
  },
  '/pricing': {
    title: 'Revont Pricing | Sales Intelligence Platform',
    description:
      'Explore Revont for B2B sales teams and request a walkthrough to discuss your team’s sales intelligence workflows and deployment needs.',
    indexable: true,
  },
  '/contact': {
    title: 'Contact Revont | See Sales Intelligence in Action',
    description:
      'Talk with Revont about live meeting coaching, deal context, workspace memory, and follow-up workflows for your sales team.',
    indexable: true,
  },
  '/privacy': {
    title: 'Privacy Policy | Revont',
    description: 'Read how Revont handles information when you use our website and services.',
    indexable: true,
  },
  '/terms': {
    title: 'Terms of Service | Revont',
    description: 'Review the terms that govern access to and use of Revont services.',
    indexable: true,
  },
  '/subprocessors': {
    title: 'Subprocessors | Revont',
    description: 'Review the service providers that help Revont deliver its services.',
    indexable: true,
  },
  '/support': {
    title: 'Support | Revont',
    description: 'Contact Revont Support or get in touch with our team.',
    indexable: false,
  },
  '/session-success': {
    title: 'You’re All Set | Revont',
    description: 'Your Revont sign-in or integration flow is complete.',
    indexable: false,
  },
  '/reset-password': {
    title: 'Reset Your Password | Revont',
    description: 'Reset your Revont account password.',
    indexable: false,
  },
  '/login': {
    title: 'Sign In | Revont',
    description: 'Open the Revont sales intelligence application.',
    indexable: false,
  },
  '/get-started': {
    title: 'Get Started | Revont',
    description: 'Continue to Revont to get started.',
    indexable: false,
  },
};

function normalizePath(pathname: string): string {
  const path = pathname.replace(/\/+$/, '');
  return path || '/';
}

function setMeta(attribute: 'name' | 'property', key: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, key);
    document.head.append(meta);
  }
  meta.content = content;
}

function setCanonical(url: string): void {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.append(canonical);
  }
  canonical.href = url;
}

export function updateRouteMetadata(pathname: string): void {
  const path = normalizePath(pathname);
  const metadata = ROUTE_METADATA[path] ?? {
    title: 'Page Not Found | Revont',
    description: 'The page you are looking for could not be found.',
    indexable: false,
  };
  const canonicalUrl = new URL(path, SITE_ORIGIN).href;

  document.documentElement.lang = 'en';
  document.title = metadata.title;
  setMeta('name', 'description', metadata.description);
  setMeta(
    'name',
    'robots',
    metadata.indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow',
  );
  setCanonical(canonicalUrl);

  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:site_name', 'Revont');
  setMeta('property', 'og:title', metadata.title);
  setMeta('property', 'og:description', metadata.description);
  setMeta('property', 'og:url', canonicalUrl);
  setMeta('property', 'og:image', SOCIAL_IMAGE);
  setMeta('property', 'og:image:secure_url', SOCIAL_IMAGE);
  setMeta('property', 'og:image:type', 'image/png');
  setMeta('property', 'og:image:width', '1201');
  setMeta('property', 'og:image:height', '628');
  setMeta('property', 'og:image:alt', 'Revont logo on a dark background');
  setMeta('property', 'og:locale', 'en_US');

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', metadata.title);
  setMeta('name', 'twitter:description', metadata.description);
  setMeta('name', 'twitter:image', SOCIAL_IMAGE);
  setMeta('name', 'twitter:image:alt', 'Revont logo on a dark background');
}
